export interface LatestRequestHandlers<T> {
  success: (value: T) => void;
  failure: (error: unknown) => void;
}

export interface LatestRequestCoordinator {
  run<T>(request: () => Promise<T>, handlers: LatestRequestHandlers<T>): Promise<void>;
}

/**
 * Runs overlapping requests while allowing only the most recently started one
 * to update client state. Request order is used instead of payload versions
 * because chat and presence can change without advancing the draft version.
 */
export function createLatestRequestCoordinator(): LatestRequestCoordinator {
  let latestSequence = 0;

  return {
    async run<T>(request: () => Promise<T>, handlers: LatestRequestHandlers<T>) {
      const sequence = ++latestSequence;
      let value: T;

      try {
        value = await request();
      } catch (error) {
        if (sequence === latestSequence) handlers.failure(error);
        return;
      }

      if (sequence === latestSequence) handlers.success(value);
    },
  };
}

export interface VerticalScrollContainer {
  scrollTop: number;
  readonly scrollHeight: number;
}

/** Scrolls only the supplied container; it never asks the page to reveal it. */
export function scrollContainerToBottom(container: VerticalScrollContainer | null): void {
  if (container) container.scrollTop = container.scrollHeight;
}

export interface DraftTeamStatus {
  id: string;
  name: string;
  withdrawn: boolean;
}

/**
 * Orders the factual team-status list without pretending to predict future
 * turns. The Durable Object recalculates draft priority after every pick, so
 * the client can authoritatively identify only the team currently on the
 * clock. All other active and completed teams are shown alphabetically.
 */
export function orderDraftTeamStatuses<T extends DraftTeamStatus>(
  teams: readonly T[],
  currentTeamId: string | null,
): T[] {
  return [...teams].sort((left, right) => {
    const leftIsCurrent = left.id === currentTeamId;
    const rightIsCurrent = right.id === currentTeamId;
    if (leftIsCurrent !== rightIsCurrent) return leftIsCurrent ? -1 : 1;
    if (left.withdrawn !== right.withdrawn) return left.withdrawn ? 1 : -1;

    const nameOrder = left.name.localeCompare(right.name, "en-US", {
      numeric: true,
      sensitivity: "base",
    });
    return nameOrder || left.id.localeCompare(right.id, "en-US");
  });
}
